import "./style.css";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getMyFriends } from "../../redux/user/userAction";
import {
  FaAngleDown,
  FaAngleUp,
  FaUserCheck,
  FaUserPlus,
  FaUserTimes,
  FaSync,
} from "react-icons/fa";
import { toast } from "react-toastify";
import {
  cancelFriendRequest,
  sendFriendRequest,
  unfriendUser,
} from "../../redux/friend/friendAction";
import { toggleInvite } from "../../redux/friend/friendReducer";
import { EmptyFriendList } from "../EmptyUI";
import UserCard from "./UserCard";

/* ------------------------- Friend Card ------------------------- */
const FriendItem = React.memo(({ friend, myId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("friends");

  const { userId, mutualFriends = 0 } = friend ?? {};

  const sendInvite = useCallback(async (type) => {
    if (!userId || !myId) return;

    if (type === "cancel") {
      await dispatch(cancelFriendRequest({ userId: myId, friendId: userId })).unwrap();
      dispatch(toggleInvite({ userId, type: "remove" }));
      setStatus("not-friends");
      toast.success("Friend request cancelled");
    } else {
      await dispatch(sendFriendRequest({ userId: myId, friendId: userId })).unwrap();
      dispatch(toggleInvite({ userId, type: "add" }));
      setStatus("request-sent");
      toast.success("Friend request sent");
    }
  }, [dispatch, userId, myId]);

  const handleFriendAction = useCallback(async () => {
    if (loading) return;

    setLoading(true);
    try {
      if (status === "friends") {
        await dispatch(unfriendUser({ userId: myId, friendId: userId })).unwrap();
        dispatch(toggleInvite({ userId, type: "remove" }));
        setStatus("not-friends");
        toast.success("Friend removed");
      } else if (status === "request-sent") {
        await sendInvite("cancel");
      } else {
        await sendInvite("sent");
      }
    } catch (error) {
      toast.error(error.message || "Failed to perform action");
    } finally {
      setLoading(false);
    }
  }, [status, loading, dispatch, myId, userId, sendInvite]);

  const buttonConfig = useMemo(() => {
    switch (status) {
      case "friends":
        return { icon: <FaUserTimes className="w-4 h-4" />, text: "Unfriend", loadingText: "Removing...", className: "bg-customGray text-white" };
      case "request-sent":
        return { icon: <FaUserCheck className="w-4 h-4" />, text: "Sent", loadingText: "Cancelling...", className: "bg-customGold text-black" };
      default:
        return { icon: <FaUserPlus className="w-4 h-4" />, text: "Add", loadingText: "Sending...", className: "bg-customGray text-white" };
    }
  }, [status]);

  return (
    <UserCard
      user={friend}
      meta={
        mutualFriends > 0 && (
          <p className="text-gray-400 text-xs mt-1">
            {mutualFriends} mutual friend{mutualFriends !== 1 ? 's' : ''}
          </p>
        )
      }
      renderActions={() => (
        <button
          disabled={loading}
          onClick={handleFriendAction}
          className={`w-full flex items-center justify-center gap-2 py-2 rounded text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${buttonConfig.className}`}
        >
          {loading ? (
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            buttonConfig.icon
          )}
          {loading ? buttonConfig.loadingText : buttonConfig.text}
        </button>
      )}
    />
  );
});

/* ------------------------- Search Bar ------------------------- */
const FriendSearchFilter = ({ searchTerm, onSearchChange, sortBy, onSortChange, totalFriends }) => {
  return (
    <div className="flex flex-col sm:flex-row gap-3 mb-4 sm:mb-6">
      <div className="flex-1 relative">
        <input
          type="text"
          placeholder={`Search ${totalFriends} friends...`}
          value={searchTerm}
          onChange={onSearchChange}
          className="w-full bg-customDark text-white px-4 py-2 pl-10 rounded-lg border border-customGray focus:outline-none focus:ring-2 focus:ring-customGold focus:border-transparent placeholder-gray-400"
          aria-label="Search friends"
        />
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>

      <select
        value={sortBy}
        onChange={onSortChange}
        className="sm:w-48 bg-customDark text-white px-4 py-2 rounded-lg border border-customGray focus:outline-none focus:ring-2 focus:ring-customGold focus:border-transparent"
        aria-label="Sort friends by"
      >
        <option value="recent">Recently Added</option>
        <option value="name">Name A-Z</option>
        <option value="mutual">Most Mutual</option>
      </select>
    </div>
  );
};

/* ------------------------- Main Component ------------------------- */
const loadingArray = new Array(8).fill(null);
const PAGE_SIZE = 12;

const RightSideFriend = ({ showHeader = true }) => {
  const dispatch = useDispatch();
  const { myFriends, friendLoading, userData } = useSelector((state) => state.user);
  const [visibleFriends, setVisibleFriends] = useState(PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("recent");

  // Fetch once, only when this list hasn't already been loaded elsewhere -
  // avoids re-requesting every time this component mounts across the app
  // (Find Friends, Settings, Chat sidebar, public profile all render it).
  useEffect(() => {
    if (myFriends.length === 0 && !friendLoading) {
      dispatch(getMyFriends());
    }
  }, [dispatch, myFriends.length, friendLoading]);

  const filteredAndSortedFriends = useMemo(() => {
    const filtered = myFriends.filter(friend =>
      friend.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      friend.lastName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    switch (sortBy) {
      case "name":
        return [...filtered].sort((a, b) => a.firstName.localeCompare(b.firstName));
      case "mutual":
        return [...filtered].sort((a, b) => (b.mutualFriends || 0) - (a.mutualFriends || 0));
      default:
        return filtered;
    }
  }, [myFriends, searchTerm, sortBy]);

  const displayedFriends = useMemo(() =>
    filteredAndSortedFriends.slice(0, visibleFriends),
    [filteredAndSortedFriends, visibleFriends]
  );

  const gridData = friendLoading && myFriends.length === 0 ? loadingArray : displayedFriends;

  const loadMoreFriends = useCallback(() => setVisibleFriends(prev => prev + PAGE_SIZE), []);
  const seeLessFriends = useCallback(() => setVisibleFriends(PAGE_SIZE), []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setVisibleFriends(PAGE_SIZE);
  }, []);

  const handleSortChange = useCallback((e) => setSortBy(e.target.value), []);

  const hasMoreFriends = visibleFriends < filteredAndSortedFriends.length;
  const showSeeLess = visibleFriends > PAGE_SIZE;

  return (
    <div className="bg-customGray shadow shadow-black/80 rounded-2xl p-4 sm:p-6 flex flex-col h-full w-full overflow-y-auto scroll-hidden border border-customGray">
      {showHeader && (
        <div className="flex items-center justify-between mb-2 sm:mb-3">
          <div>
            <h1 className="text-white text-sm sm:text-[14px] font-bold">
              My Friends{myFriends.length > 0 && ` (${myFriends.length})`}
            </h1>
            <p className="text-gray-400 text-xs sm:text-[12px]">
              People already in your circle
            </p>
          </div>
          <button
            onClick={() => dispatch(getMyFriends())}
            disabled={friendLoading}
            className="text-gray-400 hover:text-customGold transition-colors disabled:opacity-50 disabled:cursor-not-allowed p-2"
            aria-label="Refresh friends"
          >
            <FaSync className={`text-sm ${friendLoading ? "animate-spin" : ""}`} />
          </button>
        </div>
      )}

      {(myFriends.length > 0 || searchTerm) && (
        <FriendSearchFilter
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          sortBy={sortBy}
          onSortChange={handleSortChange}
          totalFriends={myFriends.length}
        />
      )}

      {gridData.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {gridData.map((friend, index) => (
            <FriendItem key={friend?.userId || index} friend={friend} myId={userData?.userId} />
          ))}
        </div>
      )}

      {!friendLoading && myFriends.length === 0 && (
        <div className="py-8">
          <EmptyFriendList />
        </div>
      )}

      {!friendLoading && searchTerm && myFriends.length > 0 && displayedFriends.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-400 text-base mb-4">
            No friends found matching "{searchTerm}"
          </p>
          <button
            onClick={() => setSearchTerm("")}
            className="px-6 py-2 bg-customGold text-black rounded-lg hover:bg-amber-500 transition-colors focus:outline-none focus:ring-2 focus:ring-customGold"
          >
            Clear Search
          </button>
        </div>
      )}

      {(hasMoreFriends || showSeeLess) && (
        <div className="flex justify-center gap-3 mt-6">
          {hasMoreFriends && (
            <button
              onClick={loadMoreFriends}
              className="flex items-center gap-2 px-5 py-2 bg-customDark text-white rounded-full hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-customGold"
            >
              Load More <FaAngleDown />
            </button>
          )}
          {showSeeLess && (
            <button
              onClick={seeLessFriends}
              className="flex items-center gap-2 px-5 py-2 bg-customDark text-white rounded-full hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-customGold"
            >
              See Less <FaAngleUp />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(RightSideFriend);
